import { useRef, useState, useMemo, useEffect } from 'react';
import { v4 } from 'uuid';
import { useProxiedRef } from '../../utils/hooks/useProxiedRef.js';
import debounce from 'lodash-es/debounce';
import { getId } from '../Listbox/ScrollableList.js';
import { createCustomKeyboardEvent } from '../../utils/input.js';
import { useFlattenedData, filterData, getIndexFromValue, setInputValueByRef, findByValue, sanitizeItem, getOptionParents } from '../Listbox/util.js';

const debouncer = /*#__PURE__*/debounce(f => f(), 200);

const convertToInputValue = value => String(value !== null && value !== void 0 ? value : '');

const useCombobox = ({
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  data: unfilteredData = [],
  defaultValue,
  disabled,
  id: nativeId,
  inline,
  loading: __,
  onChange,
  onClick,
  onKeyDown,
  onSearch,
  readOnly,
  value,
  ...props
}, ref) => {
  const inputRef = useProxiedRef(ref);
  const buttonRef = useRef(null);
  const listRef = useRef(null);
  const [open, setOpen] = useState(false);
  const listId = useMemo(() => v4(), []);
  const [inputValue, setInputValue] = useState(convertToInputValue(value));
  const shouldFilterData = !onSearch && (!inline || inline && inputValue !== convertToInputValue(value));
  const flattenedData = useFlattenedData(unfilteredData);
  const data = useMemo(() => shouldFilterData ? filterData(flattenedData, inputValue) : flattenedData, [shouldFilterData, inputValue, flattenedData]); // listbox/select change value _with_ the index, but combobox changes on select of an index (click/enter), so we need state

  const [currentIndex, setCurrentIndex] = useState(inputValue !== undefined ? getIndexFromValue(data, inputValue) : undefined);

  const setInputValueByIndex = index => {
    if (index !== undefined) {
      const option = data[index];

      if (option && !option.disabled) {
        setInputValueByRef(inputRef.current, option.value, 'focusout');
      }
    }
  };

  const setCurrentValue = index => {
    if (index === undefined) {
      return;
    }

    const option = data[index]; // if the selected option is not already selected, trigger blur event

    if (option.value !== value) {
      setInputValueByIndex(index);
    } else {
      // if the selected option is already selected, refill input with its value
      setInputValue(convertToInputValue(value));
    }
  }; // ensure the external value is synced with the internal value when mounting, e.g. incase a default value was set


  useEffect(() => {
    if (defaultValue && !value) {
      setInputValueByIndex(getIndexFromValue(data, defaultValue));
    }
  }, [data]); // update input value if it changed 'externally', e.g. clicking/entering an item in the listbox, from a modal etc

  useEffect(() => {
    if (value !== undefined && value !== inputValue) {
      setInputValue(convertToInputValue(value));
    }
  }, [value]);
  useEffect(() => {
    if (onSearch) {
      debouncer(() => {
        onSearch(inputValue);
      });
    }
  }, [inputValue]); // show listbox based on input value

  useEffect(() => {
    // don't show the popover if the internal (input) value already is the current value
    // this prevents the popover showing after selecting a value or pressing escape
    const isCurrentValue = value !== undefined && value !== null && inputValue === String(value);

    if (inputValue && data.length && !isCurrentValue) {
      setCurrentIndex(0);

      if (!open) {
        setOpen(true);
      }
    } else {
      setOpen(false);
    }
  }, [inputValue, data]);
  useEffect(() => {
    if (open) {
      setCurrentIndex(getIndexFromValue(data, inputValue) || 0);
    } else {
      setCurrentIndex(undefined);
    }
  }, [open]); // event handlers

  const handleInputBlur = event => {
    event.persist();

    if (event.relatedTarget === listRef.current) {
      event.preventDefault();
      return;
    }

    if (onChange && event.target.value !== value) {
      const item = findByValue(flattenedData, event.target.value);
      event.detail = sanitizeItem(item);
      const parents = getOptionParents(flattenedData, item === null || item === void 0 ? void 0 : item.path);

      if (parents !== null && parents.length > 0) {
        event.detail.parents = parents;
      }

      onChange(event);
    }

    if (props.onBlur) {
      props.onBlur(event);
    }
  };

  const handleInputChange = event => {
    setInputValue(event.target.value);
  };

  const handleInputClick = event => {
    if (inline || !open && inputValue && data.length) {
      setOpen(true);
    }

    if (onClick) {
      event.persist();
      onClick(event);
    }
  };

  const handleInputKeyDown = event => {
    event.persist();

    switch (event.key) {
      case 'Backspace':
        {
          return;
        }

      case 'Escape':
        {
          event.preventDefault();
          setInputValue(convertToInputValue(value));
          setOpen(false);
          return;
        }

      case 'Tab':
      case 'Enter':
        {
          if (event.key !== 'Tab') {
            event.preventDefault();
          }

          setCurrentValue(currentIndex);
          setOpen(false);
          return;
        }

      case 'ArrowDown':
        if (open) {
          event.preventDefault();
        } else {
          if (!inline && buttonRef.current) {
            buttonRef.current.click();
          }
        }

        break;

      case 'ArrowUp':
      case 'Home':
      case 'End':
        {
          if (open) {
            event.preventDefault();
          }

          break;
        }
    } // we aren't focused on the list, so manually forward the keydown event to it


    if (listRef.current) {
      listRef.current.dispatchEvent(createCustomKeyboardEvent(event));
    }

    if (inline && !open) {
      if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
        event.preventDefault();
        const initialIndex = event.key === 'ArrowUp' ? data.length - 1 : 0;
        setCurrentIndex(currentIndex !== undefined ? currentIndex : initialIndex);
        setOpen(true);
      }
    }

    if (!event.isDefaultPrevented() && onKeyDown) {
      event.persist();
      onKeyDown(event);
    }
  };

  const handleListboxChange = index => {
    setCurrentIndex(index);
  };

  const handleListboxClick = (event, index) => {
    event.preventDefault();
    setCurrentValue(index);
    setOpen(false);
  };

  const combobox = {
    'aria-expanded': open,
    'aria-owns': listId,
    'aria-haspopup': 'listbox',
    role: 'combobox'
  };
  const input = { ...props,
    'aria-controls': listId,
    // Indicates that the autocomplete behavior of the text input is to suggest a list of possible values in a popup and that the suggestions
    // are related to the string that is present in the textbox
    'aria-autocomplete': 'list',
    // Enables assistive technologies to know which element the application regards as focused while DOM focus remains on the input element
    'aria-activedescendant': currentIndex !== undefined && data[currentIndex] ? getId(listId, String(data[currentIndex].value)) : undefined,
    'aria-labelledby': ariaLabelledBy,
    disabled,
    onBlur: !disabled && !readOnly ? handleInputBlur : undefined,
    onChange: !disabled && !readOnly ? handleInputChange : undefined,
    onClick: !disabled && !readOnly ? handleInputClick : undefined,
    onKeyDown: !disabled && !readOnly ? handleInputKeyDown : undefined,
    readOnly,
    ref: inputRef,
    type: 'text',
    value: inputValue !== null && inputValue !== void 0 ? inputValue : ''
  };
  const list = {
    'aria-labelledby': ariaLabelledBy,
    data,
    disabled,
    id: listId,
    onChange: handleListboxChange,
    onClick: handleListboxClick,
    ref: listRef,
    scrollOnFocus: false,
    tabIndex: -1,
    value: currentIndex
  };
  const button = {
    ref: buttonRef
  };
  return {
    combobox,
    input,
    list,
    button,
    popover: {
      open,
      onOpenChange: setOpen
    }
  };
};

export { useCombobox };
//# sourceMappingURL=useCombobox.js.map
